import React, { useState, useEffect } from 'react';
import MetricCard from './MetricCard';
import ChartCard from './ChartCard';
import ActivityFeed from './ActivityFeed';
import { apiService } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

interface OperationsDashboardProps {
  className?: string;
}

const OperationsDashboard: React.FC<OperationsDashboardProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<any>({});
  const [personalRevenueData, setPersonalRevenueData] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    loadDashboardData();
  }, [period]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load metrics
      const metricsResponse = await apiService.dashboard.getMetrics(period);
      setMetrics(metricsResponse.data.data);

      // Load chart data
      const [revenueResponse, activityResponse] = await Promise.all([
        apiService.dashboard.getChartData('revenue', period),
        apiService.dashboard.getRecentActivity(8)
      ]);

      setPersonalRevenueData(revenueResponse.data.data?.data || []);
      setActivities(activityResponse.data.data?.activities || []);
    } catch (error) {
      console.error('Error loading operations dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceBadge = (ranking: number) => {
    if (ranking <= 3) {
      return (
        <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-meta-3 text-white">
          🏆 Top Performer
        </div>
      );
    } else if (ranking <= 10) {
      return (
        <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary text-white">
          ⭐ High Performer
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-black dark:text-white">
            My Performance Dashboard
          </h2>
          <p className="text-meta-6">
            Welcome back, {user?.firstName}! Here's your performance overview
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded border border-stroke bg-transparent py-2 px-3 text-black dark:text-white dark:border-form-strokedark dark:bg-form-input focus:border-primary focus:outline-none"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      {/* Performance Badge */}
      {metrics.performanceRanking && (
        <div className="flex justify-center">
          {getPerformanceBadge(metrics.performanceRanking)}
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="My Sales"
          value={metrics.personalSales || 0}
          valuePrefix="₹"
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          }
        />

        <MetricCard
          title="Vendors Added"
          value={metrics.vendorsAdded || 0}
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-meta-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 1 1 0 7.292M15 21H3v-1a6 6 0 0 1 12 0v1Zm0 0h6v-1a6 6 0 0 0-9-5.197m13.5-9a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
            </svg>
          }
        />

        <MetricCard
          title="Orders Placed"
          value={metrics.ordersPlaced || 0}
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-meta-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 0 0-8 0v4M5 9h14l1 12H4L5 9Z" />
            </svg>
          }
        />

        <MetricCard
          title="Performance Rank"
          value={metrics.performanceRanking || 'N/A'}
          valueSuffix={typeof metrics.performanceRanking === 'number' ? getOrdinalSuffix(metrics.performanceRanking) : ''}
          loading={loading}
          icon={
            <svg className="w-6 h-6 text-meta-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM12 14a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7Z" />
            </svg>
          }
        />
      </div>

      {/* Performance Chart */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard
          title="My Sales Performance"
          data={personalRevenueData}
          type="area"
          loading={loading}
          xDataKey="date"
          yDataKey="revenue"
          color="#10B981"
          dateFormat="daily"
          valueType="currency"
          formatTooltip={(value, name) => [`₹${value.toLocaleString()}`, 'Sales']}
        />

        {/* Monthly Target Progress */}
        <div className="rounded-sm border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
          <h4 className="text-xl font-semibold text-black dark:text-white mb-4">
            Monthly Progress
          </h4>
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-black dark:text-white">Sales Target</span>
                <span className="text-sm text-meta-6">
                  {metrics.monthlyTargetProgress || 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 dark:bg-gray-700">
                <div 
                  className="bg-primary h-3 rounded-full transition-all duration-300" 
                  style={{ width: `${Math.min(metrics.monthlyTargetProgress || 0, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-meta-6 mt-1">
                ₹{((metrics.personalSales || 0) * 100 / (metrics.monthlyTargetProgress || 1)).toLocaleString()} / ₹50,000 target
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-lg font-semibold text-black dark:text-white">
                  {new Date().getDate()}
                </p>
                <p className="text-xs text-meta-6">Days Left</p>
              </div>
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-lg font-semibold text-black dark:text-white">
                  #{metrics.performanceRanking || 'N/A'}
                </p>
                <p className="text-xs text-meta-6">Team Rank</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Tasks & Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Pending Tasks */}
        <div className="rounded-sm border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
          <h4 className="text-xl font-semibold text-black dark:text-white mb-4">
            Pending Tasks
          </h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-meta-1 rounded-full"></div>
                <span className="text-sm">Vendor Confirmations</span>
              </div>
              <span className="text-sm font-medium text-meta-1">
                {metrics.pendingVendorConfirmations || 0}
              </span>
            </div>

            {(metrics.pendingVendorConfirmations || 0) > 0 && (
              <button className="w-full text-sm text-primary hover:underline text-left">
                View pending confirmations →
              </button>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <ActivityFeed
            activities={activities}
            loading={loading}
            title="My Recent Activity"
            maxItems={6}
          />
        </div>
      </div>
    </div>
  );
};

// Helper function to get ordinal suffix
function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j == 1 && k != 11) {
    return "st";
  }
  if (j == 2 && k != 12) {
    return "nd";
  }
  if (j == 3 && k != 13) {
    return "rd";
  }
  return "th";
}

export default OperationsDashboard;